// src/screens/Admin/LangCountryStats.js
import React, { useEffect, useMemo, useState } from 'react';
import './LangCountryStats.css';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import backIcon from '../../assets/backspace-icon.png';
import { ISO_COUNTRY_CODES } from '../../constants/countryCodes';
import LangCountryActivity from './LangCountryActivity';


// ✅ Profile-language list (same as AboutSection.js)
const BASE_LANG_OPTIONS = [
  'English', 'Español', 'Français', 'Português', '中文', '日本語', '한국어', 'Deutsch',
  'Italiano', 'Indonesia', 'Tiếng Việt', 'Русский', 'हिन्दी', 'Türkçe', 'فارسی', 'العربية',
  'Swahili', 'Zulu', 'Xhosa'
];

// ✅ If a custom item has > 1 user, promote it into the official ranked list
const PROMOTE_MIN_USERS = 2;

function pctFmt(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return '0.00%';
  return `${x.toFixed(2)}%`;
}

function normalizeKey(s) {
  return String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

export default function LangCountryStats() {
  const navigate = useNavigate();

const [view, setView] = useState('overview');

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [data, setData] = useState(null);

  const officialLanguages = useMemo(() => {
    return BASE_LANG_OPTIONS.slice().sort((a, b) => a.localeCompare(b));
  }, []);

  const officialCountries = useMemo(() => {
    const dn = new Intl.DisplayNames(['en'], { type: 'region' });
    return ISO_COUNTRY_CODES
      .map((code) => dn.of(code) || code)
      .filter((name) => name && name.length > 1)
      .sort((a, b) => a.localeCompare(b));
  }, []);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        setLoading(true);
        setErr('');
        const res = await api.get('/admin/metrics/lang-country');
        if (!mounted) return;
        setData(res.data);
      } catch (e) {
        console.error(e);
        if (!mounted) return;
        setErr(e?.response?.data?.error || 'Failed to load Language & Country stats.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  // ✅ read backend shape: { summary, breakdown: { languages, countries } }
  const maps = useMemo(() => {
    const totalUsers = data?.summary?.totalUsers || 0;

    const langMap = new Map();    // key -> { name, users, pct }
    const countryMap = new Map();

    (data?.breakdown?.languages || []).forEach((row) => {
      const key = normalizeKey(row?.name);
      langMap.set(key, {
        name: row?.name || '',
        users: row?.users || 0,
        pct: row?.pct || 0
      });
    });

    (data?.breakdown?.countries || []).forEach((row) => {
      const key = normalizeKey(row?.name);
      countryMap.set(key, {
        name: row?.name || '',
        users: row?.users || 0,
        pct: row?.pct || 0
      });
    });

    return { totalUsers, langMap, countryMap };
  }, [data]);

  // ✅ Promote popular custom values into the official list (ranked together)
  const tables = useMemo(() => {
    const { langMap, countryMap } = maps;

    const officialLangKeys = new Set(officialLanguages.map(normalizeKey));
    const officialCountryKeys = new Set(officialCountries.map(normalizeKey));

    // --- LANGUAGES ---
    const promotedLang = Array.from(langMap.entries())
      .filter(([key, v]) => !officialLangKeys.has(key) && (v?.users || 0) >= PROMOTE_MIN_USERS)
      .map(([, v]) => v?.name)
      .filter(Boolean);

    const mergedLanguageNames = Array.from(
      new Set([...officialLanguages, ...promotedLang].map((x) => String(x).trim()))
    );

    const officialLanguageRows = mergedLanguageNames
      .map((name) => {
        const key = normalizeKey(name);
        const found = langMap.get(key);
        return { name, users: found?.users || 0, pct: found?.pct || 0 };
      })
      .sort((a, b) => b.users - a.users || a.name.localeCompare(b.name));

    const customLanguageRows = Array.from(langMap.entries())
      .filter(([key, v]) => {
        if (officialLangKeys.has(key)) return false;              // already official
        if ((v?.users || 0) >= PROMOTE_MIN_USERS) return false;  // promoted -> not custom anymore
        return true;
      })
      .map(([, v]) => ({ name: v.name, users: v.users, pct: v.pct }))
      .sort((a, b) => b.users - a.users || a.name.localeCompare(b.name));

    // --- COUNTRIES ---
    const promotedCountry = Array.from(countryMap.entries())
      .filter(([key, v]) => !officialCountryKeys.has(key) && (v?.users || 0) >= PROMOTE_MIN_USERS)
      .map(([, v]) => v?.name)
      .filter(Boolean);

    const mergedCountryNames = Array.from(
      new Set([...officialCountries, ...promotedCountry].map((x) => String(x).trim()))
    );

    const officialCountryRows = mergedCountryNames
      .map((name) => {
        const key = normalizeKey(name);
        const found = countryMap.get(key);
        return { name, users: found?.users || 0, pct: found?.pct || 0 };
      })
      .sort((a, b) => b.users - a.users || a.name.localeCompare(b.name));

    const customCountryRows = Array.from(countryMap.entries())
      .filter(([key, v]) => {
        if (officialCountryKeys.has(key)) return false;
        if ((v?.users || 0) >= PROMOTE_MIN_USERS) return false;
        return true;
      })
      .map(([, v]) => ({ name: v.name, users: v.users, pct: v.pct }))
      .sort((a, b) => b.users - a.users || a.name.localeCompare(b.name));

    return {
      officialLanguageRows,
      customLanguageRows,
      officialCountryRows,
      customCountryRows,
      promotedLangCount: promotedLang.length,
      promotedCountryCount: promotedCountry.length
    };
  }, [maps, officialLanguages, officialCountries]);

  const summary = data?.summary || {};
  const totalUsers = summary.totalUsers || 0;

  return (
    <div className="langcountry-screen">
      <div className="langcountry-header">
        <img src={backIcon} alt="Back" onClick={() => navigate(-1)} />
        <h2>Language &amp; Country</h2>
      </div>


<div className="langcountry-body">
  <div className="langcountry-view-controls" role="group" aria-label="Report view">
    <button
      type="button"
      aria-pressed={view === 'overview'}
      onClick={() => setView('overview')}
    >
      Overview
    </button>

    <button
      type="button"
      aria-pressed={view === 'activity'}
      onClick={() => setView('activity')}
    >
      Activity
    </button>
  </div>

  {view === 'activity' ? (
    <LangCountryActivity />
  ) : loading ? (

          <div className="langcountry-loading">Loading…</div>
        ) : err ? (
          <div className="langcountry-error">{err}</div>
        ) : (
          <>
            <div className="langcountry-summary">
              <div className="sum-card">
                <div className="sum-label">Total Users</div>
                <div className="sum-value">{totalUsers}</div>
              </div>

              <div className="sum-card">
                <div className="sum-label">Has Country</div>
                <div className="sum-value">{summary.usersWithCountry || 0}</div>
                <div className="sum-sub">
                  {pctFmt(totalUsers ? (summary.usersWithCountry / totalUsers) * 100 : 0)}
                </div>
              </div>

              <div className="sum-card">
                <div className="sum-label">Has Languages</div>
                <div className="sum-value">{summary.usersWithLanguages || 0}</div>
                <div className="sum-sub">
                  {pctFmt(totalUsers ? (summary.usersWithLanguages / totalUsers) * 100 : 0)}
                </div>
              </div>

              <div className="sum-card">
                <div className="sum-label">Has Both</div>
                <div className="sum-value">{summary.usersWithBoth || 0}</div>
                <div className="sum-sub">
                  {pctFmt(totalUsers ? (summary.usersWithBoth / totalUsers) * 100 : 0)}
                </div>
              </div>
            </div>

            <div className="langcountry-grid">
              {/* LANGUAGES */}
              <div className="panel">
                <div className="panel-title">
                  Languages (Ranked)
                  {tables.promotedLangCount > 0 ? (
                    <span style={{ opacity: 0.7, marginLeft: 8, fontWeight: 600 }}>
                      +{tables.promotedLangCount} promoted
                    </span>
                  ) : null}
                </div>

                <div className="rows">
                  {tables.officialLanguageRows.map((r) => (
                    <div className="row" key={`lang-${normalizeKey(r.name)}`}>
                      <div className="row-left">
                        <div className="row-name">{r.name}</div>
                        <div className="row-sub">{r.users} users</div>
                      </div>
                      <div className="row-right">{pctFmt(r.pct)}</div>
                    </div>
                  ))}
                </div>

                {tables.customLanguageRows.length > 0 && (
                  <>
                    <div className="panel-subtitle">Custom / Typed (1 user)</div>
                    <div className="rows">
                      {tables.customLanguageRows.map((r) => (
                        <div className="row" key={`lang-custom-${normalizeKey(r.name)}`}>
                          <div className="row-left">
                            <div className="row-name">{r.name}</div>
                            <div className="row-sub">{r.users} users</div>
                          </div>
                          <div className="row-right">{pctFmt(r.pct)}</div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* COUNTRIES */}
              <div className="panel">
                <div className="panel-title">
                  Countries (Ranked)
                  {tables.promotedCountryCount > 0 ? (
                    <span style={{ opacity: 0.7, marginLeft: 8, fontWeight: 600 }}>
                      +{tables.promotedCountryCount} promoted
                    </span>
                  ) : null}
                </div>

                <div className="rows">
                  {tables.officialCountryRows.map((r) => (
                    <div className="row" key={`country-${normalizeKey(r.name)}`}>
                      <div className="row-left">
                        <div className="row-name">{r.name}</div>
                        <div className="row-sub">{r.users} users</div>
                      </div>
                      <div className="row-right">{pctFmt(r.pct)}</div>
                    </div>
                  ))}
                </div>

                {tables.customCountryRows.length > 0 && (
                  <>
                    <div className="panel-subtitle">Custom / Typed (1 user)</div>
                    <div className="rows">
                      {tables.customCountryRows.map((r) => (
                        <div className="row" key={`country-custom-${normalizeKey(r.name)}`}>
                          <div className="row-left">
                            <div className="row-name">{r.name}</div>
                            <div className="row-sub">{r.users} users</div>
                          </div>
                          <div className="row-right">{pctFmt(r.pct)}</div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

